import util from '../lib/util';
import newHRTimestamp from '../lib/picos/timestamp';
import type {uuid, SchemaField, EntitySchema} from "../@types/schema";
import Base from "./base";
import {genUUID} from "../lib/picos/uuids";
import type {EntityType} from "../@types/entity";
function getCurrentUserId() { return sessionStorage.getItem('userId'); }
function getCurrentFirmId() { return sessionStorage.getItem('firmId'); }
export const AccountType = {
    Trust: "T",
    Operating: "O",
    Escrow: "E"
};
export type AccountTypeType = typeof AccountType[keyof typeof AccountType];
class BankAccount extends Base implements EntityType {
    bankAccountId: uuid;
    name: string;
    accountType: AccountTypeType;
    matterId?: uuid | null;
    notes?: string | null;
    externalAccountId?: string | null;
    externalAccountName?: string | null;
    constructor(doc: typeof BankAccount | any) {
        super(doc);
        if (!doc) doc = {
            firmId: getCurrentFirmId(),
            addedBy: getCurrentUserId(),
            addedOn: newHRTimestamp(),
            status: "C",
            entityId: null,
            entityType: null,
            bankAccountId: genUUID(),
            name: null,
            accountType: null,
        };
        // @ts-ignore
        this.entityId = doc._entityId || doc.entityId || doc.entityid || '00000000-0000-0000-0000-000000000000';
        // @ts-ignore
        this.entityType = doc._entityType || doc.entityType || doc.entitytype || "bankAccount";
        // @ts-ignore
        this.bankAccountId = doc._bankAccountId || doc.bankAccountId || doc.bankaccountid || genUUID();
        // @ts-ignore
        this.name = doc._name || doc.name || null;
        // @ts-ignore
        this.accountType = util.trimRight(doc._accountType) || util.trimRight(doc.accountType) || util.trimRight(doc.accounttype) || null;
        // @ts-ignore
        this.matterId = doc._matterId || doc.matterId || doc.matterid || null;
        // @ts-ignore
        this.notes = doc._notes || doc.notes || null;
        // @ts-ignore
        this.externalAccountId = doc._externalAccountId || doc.externalAccountId || doc.externalaccountid || null;
        // @ts-ignore
        this.externalAccountName = doc._externalAccountName || doc.externalAccountName || doc.externalaccountname || null;
    }
    get id(): uuid { return this.bankAccountId; }
    static ACCOUNT_TYPE_VALUES = ["T", "O", "E"];
    static override getPkFields(): SchemaField[] { return [{"type":"uuid","required":true,"pk":true,"default":"__UUID__","comment":"Unique ID for bankAccount"},{"type":"uuid","required":true,"pk":true,"base":true,"default":"__FIRM__","mergeStrategy":"immutable","comment":"Associated firm"}]; }
    toLowerCase() { return this.bankAccountId; }
    static override getNonRefArrayFields(): string[] { return []; }
    static override getDefaultEntries(): BankAccount[] {
        return [{"bankAccountId":"2F0B5432-35A2-44A3-A868-E9A7ABBC69D1","name":"Operating","accountType":"O","notes":"Auto-generated","status":"C"},{"bankAccountId":"B11023AD-2F6A-4C21-96D6-73BB1602595E","name":"IOLTA","accountType":"T","notes":"Auto-generated","status":"C"}].map(e => new BankAccount(e));
    }
    static override getSchema(): EntitySchema {
        return Base.withBaseFields({
    entity: 'bankAccount',
    dataArea: 'Trust and Payments',
    table: 'BANK_ACCOUNT',
    comment: 'A firm bank account used in trust accounting and financial transactions. Accounts are typed as Operating (general business), Trust (IOLTA client-fund holding), or Escrow (matter-specific third-party funds). Two accounts — Operating and IOLTA — are auto-seeded for every firm; additional escrow accounts can be linked to a specific matter. Accounts may be mapped to a corresponding account in Xero or QuickBooks Online for accounting sync.',
    fields: {
        /**
         * ID of this bank account
         */
        bankAccountId: {type: 'uuid', required: true, pk: true, default: '__UUID__', comment: 'Unique ID for bankAccount'},

        /**
         * Name of this account, for display purposes.
         */
        name: {type: 'string', required: true, comment: 'Display name of this account'},

        /**
         * Type of account.  Choices: Trust or Operating.
         */
        accountType: {
            type: 'char',
            values: ['T', 'O', 'E'],
            valueNames: ['Trust', 'Operating', 'Escrow'],
            required: true,
            comment: 'Trust (IOLTA), Operating or Escrow'
        },

        /**
         * Related matter
         */
        matterId: {type: 'uuid', comment: 'If an escrow account, what is the related matter?'},

        /**
         * Notes on the account.
         */
        notes: {type: 'string', required: false, comment: 'Internal notes on this bank account (firm-facing only)'},

        /**
         * External account ID from connected accounting software (Xero or QBO).
         * Provider-agnostic - works with either integration.
         */
        externalAccountId: {type: 'string', comment: 'External account ID from Xero or QuickBooks Online'},

        /**
         * Cached display name from the external accounting software.
         */
        externalAccountName: {type: 'string', comment: 'Cached display name from external accounting software'},
    },
    entries: [
        {bankAccountId: '2F0B5432-35A2-44A3-A868-E9A7ABBC69D1', name: 'Operating', accountType: 'O', notes: 'Auto-generated'},
        {bankAccountId: 'B11023AD-2F6A-4C21-96D6-73BB1602595E', name: 'IOLTA', accountType: 'T', notes: 'Auto-generated'}
    ],
    maps: [
        {name: 'matterId', keysFn: t => t.matterId, presentFn: (t, k) => t.matterId === k},
    ]
});
    }
}
export default BankAccount;
